import React from "react";
import { Icon } from "semantic-ui-react";
import { BubbleList, DetailSection, ResultCard, SectionTitle, StatusPill, greyBox } from "../../components/reportCards";

// "N개 중 M개 정확" — only when it is what the score was computed from. Evaluations up to
// 2026-09-02 stored the grader's own holistic score (e.g. 98 with 10/10 correct), and a
// count that disagrees with the number beside it is worse than no count.
export const claimTally = (pkEvaluation) => {
    const scored = (pkEvaluation?.extracted_claims || []).filter((c) => c.verification === "Correct" || c.verification === "Incorrect");
    if (!scored.length) return null;
    const correct = scored.filter((c) => c.verification === "Correct").length;
    return Math.abs((correct / scored.length) * 100 - pkEvaluation.score) < 0.5 ? { correct, total: scored.length } : null;
};

/** 제품 지식 as the reader sees it (#283): the share right ("86%", coloured as a score) with the
 *  counts under it ("정확 6 / 7") — never a 0–100 number on its own, which made 1/2 and 9/18
 *  look the same. Evaluations before the claim-based score (see claimTally) keep "N점". */
export const pkScoreDisplay = (pkEvaluation) => {
  if (!pkEvaluation || pkEvaluation.score == null) return null;
  const tally = claimTally(pkEvaluation);
  if (!tally) return { value: `${Math.round(pkEvaluation.score)}점`, score: pkEvaluation.score, sub: pkEvaluation.product?.name };
  const pct = Math.round((tally.correct / tally.total) * 100);
  return { value: `${pct}%`, score: pct, sub: `정확 ${tally.correct} / ${tally.total}` };
};

// The quote is copied from markdown specs: table pipes, bold and bullets read as noise.
const tidyEvidence = (text) =>
    text
        .split("\n")
        .map((line) => line.replace(/\*\*/g, "").replace(/^\s*[*-]\s+/, "").replace(/\s*\|\s*/g, " · ").replace(/^ · | · $/g, "").trim())
        .filter(Boolean)
        .join("\n");

/**
 * One extracted claim, on the same card as a 응대 스킬 criterion (components/reportCards.js):
 * number, the claim, a 정확함/오류 pill; opened — 검증 결과, 관련 말풍선 (#241), 비교한 자료
 * (#290), the source.
 */
const ClaimCard = ({ claim, idx }) => {
    const isCorrect = claim.verification === "Correct";
    const isIncorrect = claim.verification === "Incorrect";
    return (
        <ResultCard
            id={`claim_${idx}`}
            number={idx + 1}
            title={claim.text}
            pill={(isCorrect || isIncorrect) && <StatusPill favorable={isCorrect}>{isCorrect ? "정확함" : "오류"}</StatusPill>}
        >
            <DetailSection label="검증 결과">
                <div style={{ fontSize: "14px", lineHeight: 1.6, color: "#424245" }}>{claim.reasoning}</div>
            </DetailSection>
            {claim.bubbles?.length > 0 && (
                <DetailSection label={`관련 말풍선 (${claim.bubbles.length})`}>
                    <BubbleList bubbles={claim.bubbles} />
                </DetailSection>
            )}
            {/* The catalog line(s) an 오류 was judged against (#290), so the trainee can see what
                they were compared with — e.g. the Apple Store price, not a 출고가. */}
            {isIncorrect && claim.evidence && (
                <DetailSection label="비교한 자료">
                    <div style={{ ...greyBox, whiteSpace: "pre-line" }}>{tidyEvidence(claim.evidence)}</div>
                </DetailSection>
            )}
            {claim.citation && (
                <a
                    href={claim.citation}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "14px", padding: "6px 12px", borderRadius: "999px", backgroundColor: "#F2F2F7", color: "#1d1d1f", fontSize: "12px", fontWeight: 600 }}
                >
                    <Icon name="external" className="centered-icon" style={{ fontSize: "11px" }} />
                    출처 확인하기
                </a>
            )}
        </ResultCard>
    );
};

// `first`: the header opens its pane (desktop's 제품 지식 tab); on phones 평가 요약 comes first.
const ProductKnowledgeCriteriaList = ({ pkEvaluation, first = true }) => {
  if (!pkEvaluation) {
    return <p style={{ padding: '1em', color: '#86868b' }}>이 롤플레이에 대한 제품 지식 평가가 없습니다.</p>;
  }

  const { extracted_claims } = pkEvaluation;
  const tally = claimTally(pkEvaluation);

  return (
    <>
      <SectionTitle first={first} meta={tally ? `${tally.total}개 중 ${tally.correct}개 정확` : null}>추출된 주장</SectionTitle>
      {extracted_claims && extracted_claims.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {extracted_claims.map((claim, idx) => (
            <ClaimCard key={idx} claim={claim} idx={idx} />
          ))}
        </div>
      ) : (
        <p style={{ padding: '1em', color: '#86868b' }}>추출된 주장이 없습니다.</p>
      )}
    </>
  );
};

export default ProductKnowledgeCriteriaList;
